import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, CheckCheck, Settings } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { apiService } from '../services/api.service';
import { formatDistanceToNow, format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface Notification {
  id: string;
  type: 'registration_received' | 'heat_announced' | 'heat_schedule_changed' | 'results_available' | 'new_account';
  title: string;
  message?: string;
  related_event_id?: string;
  related_data?: Record<string, any>;
  is_read: boolean;
  action_url?: string;
  created_at: string;
  read_at?: string;
}

interface NotificationPreferences {
  email_registration_received: boolean;
  email_heat_announced: boolean;
  email_heat_schedule_changed: boolean;
  email_results_available: boolean;
  email_new_account: boolean;
  inapp_registration_received: boolean;
  inapp_heat_announced: boolean;
  inapp_heat_schedule_changed: boolean;
  inapp_results_available: boolean;
  inapp_new_account: boolean;
}


const NOTIFICATION_LABELS: Record<string, string> = {
  registration_received: 'Nouvelle inscription',
  heat_announced: 'Heat annoncée',
  heat_schedule_changed: 'Horaire modifié',
  results_available: 'Résultats disponibles',
  new_account: 'Nouveau compte'
};

export default function Notifications() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [savingPrefs, setSavingPrefs] = useState(false);

  // Charger les notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await apiService.getNotifications(100, 0, filter === 'unread');
      setNotifications(response.notifications || []);
    } catch (error) {
      console.error('Erreur chargement notifications:', error);
    }
  }, [filter]);

  // Charger les préférences
  const fetchPreferences = useCallback(async () => {
    try {
      const response = await apiService.getNotificationPreferences();
      setPreferences(response.preferences || null);
    } catch (error) {
      console.error('Erreur chargement préférences:', error);
    }
  }, []);

  // Charger les données
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchNotifications(), fetchPreferences()]);
      setLoading(false);
    };
    loadData();
  }, [fetchNotifications, fetchPreferences]);

  // Marquer comme lue
  const markAsRead = async (notificationId: string) => {
    try {
      await apiService.markNotificationRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
    } catch (error) {
      console.error('Erreur marquage notification:', error);
    }
  };

  // Marquer toutes comme lues
  const markAllAsRead = async () => {
    try {
      await apiService.markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error('Erreur marquage toutes notifications:', error);
    }
  };

  // Mettre à jour une préférence
  const updatePreference = async (key: string, value: boolean) => {
    if (!preferences) return;

    setSavingPrefs(true);
    try {
      const updated = await apiService.updateNotificationPreferences({ [key]: value });
      setPreferences(updated.preferences);
    } catch (error) {
      console.error('Erreur mise à jour préférence:', error);
    } finally {
      setSavingPrefs(false);
    }
  };

  // Clic sur notification
  // Le marquage est ATTENDU avant de partir : un `window.location.href`
  // immédiat rechargeait la page et annulait la requête en vol — la
  // notification restait non lue (constaté le 2026-09-04). Une URL du même
  // site se suit par le routeur, sans rechargement.
  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }
    if (!notification.action_url) return;
    const url = new URL(notification.action_url, window.location.origin);
    if (url.origin === window.location.origin) {
      navigate(url.pathname + url.search + url.hash);
    } else {
      window.location.href = notification.action_url;
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Notifications</h1>
          <p className="text-muted-foreground">
            Gère tes notifications et tes préférences
          </p>
        </div>
      </div>

      <Tabs defaultValue="notifications" className="space-y-6">
        <TabsList>
          <TabsTrigger value="notifications" className="gap-2">
            <Bell size={16} />
            Notifications
            {unreadCount > 0 && (
              <Badge variant="destructive" className="ml-1">
                {unreadCount}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="preferences" className="gap-2">
            <Settings size={16} />
            Préférences
          </TabsTrigger>
        </TabsList>

        {/* Tab Notifications */}
        <TabsContent value="notifications">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Button
                    variant={filter === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilter('all')}
                  >
                    Toutes
                  </Button>
                  <Button
                    variant={filter === 'unread' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilter('unread')}
                  >
                    Non lues ({unreadCount})
                  </Button>
                </div>
                {unreadCount > 0 && (
                  <Button variant="outline" size="sm" onClick={markAllAsRead}>
                    <CheckCheck size={16} className="mr-2" />
                    Tout marquer lu
                  </Button>
                )}
              </div>
            </CardHeader>

            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-40">
                  <span className="text-muted-foreground">Chargement...</span>
                </div>
              ) : notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-center">
                  <Bell size={32} className="text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">
                    {filter === 'unread' ? 'Aucune notification non lue' : 'Aucune notification'}
                  </p>
                </div>
              ) : (
                <div className="divide-y">
                  {notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`px-6 py-4 hover:bg-muted/50 cursor-pointer transition-colors ${
                        !notification.is_read ? 'bg-white/[0.05]' : ''
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="flex items-start gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className={`font-medium ${!notification.is_read ? 'text-foreground' : 'text-muted-foreground'}`}>
                              {notification.title}
                            </p>
                            {!notification.is_read && (
                              <div className="w-2 h-2 rounded-full bg-primary" />
                            )}
                          </div>
                          {notification.message && (
                            <p className="text-sm text-muted-foreground mb-2">
                              {notification.message}
                            </p>
                          )}
                          <div className="flex items-center gap-4 text-xs text-muted-foreground">
                            <span>
                              {formatDistanceToNow(new Date(notification.created_at), {
                                addSuffix: true,
                                locale: fr
                              })}
                            </span>
                            <span>
                              {format(new Date(notification.created_at), 'dd/MM/yyyy HH:mm', { locale: fr })}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {!notification.is_read && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification.id);
                              }}
                            >
                              <Check size={16} />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Préférences */}
        <TabsContent value="preferences">
          <Card>
            <CardHeader>
              <CardTitle>Préférences de notification</CardTitle>
              <CardDescription>
                Choisis les notifications que tu souhaites recevoir.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {!preferences ? (
                <div className="text-center py-8 text-muted-foreground">
                  Chargement des préférences...
                </div>
              ) : (
                <>
                  {/* Nouvelle inscription */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">Nouvelles inscriptions</h4>
                        <p className="text-sm text-muted-foreground">
                          Quand quelqu'un s'inscrit à l'un de tes événements
                        </p>
                      </div>
                      <Switch
                        id="inapp_registration"
                        checked={preferences.inapp_registration_received}
                        onCheckedChange={(v) => updatePreference('inapp_registration_received', v)}
                        disabled={savingPrefs}
                      />
                    </div>
                  </div>

                  <Separator />

                  {/* Heat annoncée */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">Heat annoncée</h4>
                        <p className="text-sm text-muted-foreground">
                          Quand ta heat est publiée
                        </p>
                      </div>
                      <Switch
                        id="inapp_heat"
                        checked={preferences.inapp_heat_announced}
                        onCheckedChange={(v) => updatePreference('inapp_heat_announced', v)}
                        disabled={savingPrefs}
                      />
                    </div>
                  </div>

                  <Separator />

                  {/* Horaire modifié */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">Horaire modifié</h4>
                        <p className="text-sm text-muted-foreground">
                          Quand l'horaire de ta heat change
                        </p>
                      </div>
                      <Switch
                        id="inapp_schedule"
                        checked={preferences.inapp_heat_schedule_changed}
                        onCheckedChange={(v) => updatePreference('inapp_heat_schedule_changed', v)}
                        disabled={savingPrefs}
                      />
                    </div>
                  </div>

                  <Separator />

                  {/* Résultats disponibles */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">Résultats disponibles</h4>
                        <p className="text-sm text-muted-foreground">
                          Quand les résultats d'un événement sont publiés
                        </p>
                      </div>
                      <Switch
                        id="inapp_results"
                        checked={preferences.inapp_results_available}
                        onCheckedChange={(v) => updatePreference('inapp_results_available', v)}
                        disabled={savingPrefs}
                      />
                    </div>
                  </div>

                  <Separator />

                  {/* Nouveau compte (admin only) */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium">Nouveau compte</h4>
                        <p className="text-sm text-muted-foreground">
                          Quand un nouveau compte est créé sur la plateforme (admin uniquement)
                        </p>
                      </div>
                      <Switch
                        id="inapp_account"
                        checked={preferences.inapp_new_account}
                        onCheckedChange={(v) => updatePreference('inapp_new_account', v)}
                        disabled={savingPrefs}
                      />
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
